import { useState } from 'react'
import Button from '../components/Button'

interface DataItem {
  id: string
  name: string
  type: string
  value: string
  updatedAt: string
  status: 'active' | 'inactive' | 'pending'
}

const mockData: DataItem[] = [
  { id: '1', name: 'example.com', type: 'DDNS', value: '192.168.1.1', updatedAt: '2024-12-12 10:30', status: 'active' },
  { id: '2', name: 'api.example.com', type: 'A Record', value: '10.0.0.1', updatedAt: '2024-12-11 15:20', status: 'active' },
  { id: '3', name: 'mail.example.com', type: 'MX', value: 'mail.google.com', updatedAt: '2024-12-10 09:15', status: 'inactive' },
  { id: '4', name: 'cdn.example.com', type: 'CNAME', value: 'cdn.cloudflare.com', updatedAt: '2024-12-09 14:45', status: 'pending' },
  { id: '5', name: 'dev.example.com', type: 'DDNS', value: '172.16.0.1', updatedAt: '2024-12-08 11:00', status: 'active' },
]

const statusColors = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
  pending: 'bg-yellow-100 text-yellow-800',
}

const statusLabels = {
  active: '活跃',
  inactive: '停用',
  pending: '待定',
}

export default function DataList() {
  const [data, setData] = useState<DataItem[]>(mockData)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedItems, setSelectedItems] = useState<string[]>([])

  const filteredData = data.filter(
    (item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.type.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const toggleSelect = (id: string) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const toggleSelectAll = () => {
    if (selectedItems.length === filteredData.length) {
      setSelectedItems([])
    } else {
      setSelectedItems(filteredData.map((item) => item.id))
    }
  }

  const handleDelete = (id: string) => {
    setData((prev) => prev.filter((item) => item.id !== id))
    setSelectedItems((prev) => prev.filter((i) => i !== id))
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* 页面标题 */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">数据管理</h1>
          <p className="text-sm text-gray-500 mt-1">管理你收集的所有数据</p>
        </div>
        <Button>➕ 新增数据</Button>
      </div>

      {/* 搜索和筛选 */}
      <div className="card-gradient rounded-xl shadow-soft border border-gray-100 p-3 md:p-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="搜索数据..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 bg-gray-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm transition-colors duration-200"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              🔍
            </span>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1 sm:flex-initial">筛选</Button>
            <Button variant="secondary" className="flex-1 sm:flex-initial">导出</Button>
          </div>
        </div>
      </div>

      {/* 移动端卡片视图 */}
      <div className="lg:hidden space-y-3">
        {filteredData.map((item, index) => (
          <div key={item.id} className="card-gradient rounded-xl shadow-soft border border-gray-100 p-4 hover-lift animate-fade-in-up" style={{ animationDelay: `${index * 50}ms` }}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-3 flex-1">
                <input
                  type="checkbox"
                  checked={selectedItems.includes(item.id)}
                  onChange={() => toggleSelect(item.id)}
                  className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 mb-1 break-all">{item.name}</h3>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-2">
                    <span className="bg-gray-100 px-2 py-1 rounded">{item.type}</span>
                    <span className={`px-2 py-1 rounded ${statusColors[item.status]}`}>
                      {statusLabels[item.status]}
                    </span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <code className="block bg-gray-100 px-2 py-1 rounded text-xs break-all">{item.value}</code>
                    <span className="text-xs text-gray-500">{item.updatedAt}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
              <button className="p-2 text-gray-400 hover:text-primary-600">
                ✏️
              </button>
              <button
                onClick={() => handleDelete(item.id)}
                className="p-2 text-gray-400 hover:text-red-600"
              >
                🗑️
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* 桌面端表格视图 */}
      <div className="hidden lg:block bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left">
                <input
                  type="checkbox"
                  checked={selectedItems.length === filteredData.length && filteredData.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                名称
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                类型
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                值
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                状态
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                更新时间
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredData.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(item.id)}
                    onChange={() => toggleSelect(item.id)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </td>
                <td className="px-6 py-4">
                  <span className="font-medium text-gray-900">{item.name}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-600">{item.type}</span>
                </td>
                <td className="px-6 py-4">
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">{item.value}</code>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusColors[item.status]}`}>
                    {statusLabels[item.status]}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-500">{item.updatedAt}</span>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button className="p-1 text-gray-400 hover:text-primary-600">
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* 分页 */}
        <div className="px-4 md:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-gray-500">
            显示 {filteredData.length} 条，共 {data.length} 条
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm">上一页</Button>
            <Button variant="ghost" size="sm">下一页</Button>
          </div>
        </div>
      </div>
    </div>
  )
}
